
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { OrganizationDetails, Answer } from "@/types/assessment";
import { generateAssessmentData, saveAssessmentData } from "@/utils/assessment-operations";

interface UseSaveAssessmentProps {
  organizationDetails: OrganizationDetails | null;
  answers: Answer[];
  assessmentResults: {
    dimensionScores: any[];
    overallScore: number;
    report: {
      maturityLevel: string;
      description: string;
    };
    dimensionScoresRecord: Record<string, number>;
  } | null;
}

export const useSaveAssessment = ({ 
  organizationDetails, 
  answers, 
  assessmentResults 
}: UseSaveAssessmentProps) => {
  const [savedAssessmentId, setSavedAssessmentId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const saveAssessment = async () => {
    if (!assessmentResults || isSaving || !organizationDetails || !user?.email) {
      toast({
        title: "Cannot save assessment",
        description: "Missing data, already saving, or not logged in",
        variant: "destructive"
      });
      return { success: false };
    }

    setIsSaving(true);
    
    try {
      // Generate assessment data 
      const assessmentData = generateAssessmentData(
        organizationDetails,
        answers,
        user.email
      );
      
      console.log("Saving assessment with user email:", user.email);
      
      // Save the assessment data and handle any errors
      const result = await saveAssessmentData(assessmentData);
      
      // Update the saved assessment ID
      if (result.data?.id) {
        setSavedAssessmentId(result.data.id);
      }
      
      toast({
        title: "Assessment saved",
        description: "Your assessment has been saved successfully."
      });
      
      return { success: true, assessmentId: result.data?.id };
    } catch (error: any) {
      console.error('Failed to save assessment:', error);
      
      toast({
        title: "Error saving assessment",
        description: error.message || "Unable to save your assessment",
        variant: "destructive"
      });
      return { success: false, error: error.message };
    } finally {
      setIsSaving(false);
    }
  };

  return {
    savedAssessmentId,
    isSaving,
    saveAssessment
  };
};
